// Loader for the SwiftLaTeX PdfTeX WASM engine (vendored in /public/swiftlatex).
// TeXLive packages are fetched on demand via our API proxy (disk-cached on the server).

let scriptPromise = null;
let sharedEngine = null;
let enginePromise = null;
let warmupPromise = null;
let compileChain = Promise.resolve();

const withCompileLock = (fn) => {
  const run = compileChain.then(() => fn());
  compileChain = run.catch(() => {});
  return run;
};

const API_BASE = process.env.REACT_APP_API_URL || 'https://quibly.onrender.com';
const DEFAULT_TEXLIVE_ENDPOINT = `${API_BASE.replace(/\/$/, '')}/texlive/`;

// Pull in the same packages/fonts as resume templates (incl. helvet bold/italic).
const WARMUP_LATEX = `\\documentclass[letterpaper,11pt]{article}
\\usepackage[empty]{fullpage}
\\usepackage{titlesec}
\\usepackage{enumitem}
\\usepackage[hidelinks]{hyperref}
\\usepackage{xcolor}
\\usepackage{tabularx}
\\usepackage[T1]{fontenc}
\\usepackage{helvet}
\\renewcommand{\\familydefault}{\\sfdefault}
\\titleformat{\\section}{\\bfseries\\vspace{2pt}\\raggedright\\large}{}{0em}{}[\\titlerule]
\\begin{document}
\\section{Warmup}
\\begin{tabularx}{\\textwidth}{X r}
\\textbf{Bold label} & Jan 2020 \\\\
\\textit{Italic note} & Present \\\\
\\end{tabularx}
\\end{document}`;

const getTexliveEndpoint = () => {
  const configured = process.env.REACT_APP_TEXLIVE_URL;
  if (configured) {
    return configured.endsWith('/') ? configured : `${configured}/`;
  }
  return DEFAULT_TEXLIVE_ENDPOINT;
};

const normalizeLatex = (latexSrc) => {
  if (typeof latexSrc !== 'string') return '';
  return latexSrc
    .replace(/\r\n/g, '\n')
    .replace(/\u2018|\u2019/g, "'")
    .replace(/\u201c|\u201d/g, '"')
    .replace(/\u2013|\u2014/g, '--')
    .trim();
};

const validateLatex = (latexSrc) => {
  if (!latexSrc) {
    throw new Error('LaTeX source is empty');
  }
  if (!latexSrc.includes('\\documentclass')) {
    throw new Error('Invalid LaTeX source: missing \\documentclass');
  }
  if (!latexSrc.includes('\\begin{document}')) {
    throw new Error('Invalid LaTeX source: missing \\begin{document}');
  }
  if (!latexSrc.includes('\\end{document}')) {
    throw new Error('Invalid LaTeX source: missing \\end{document}');
  }
};

const loadScript = () => {
  if (window.PdfTeXEngine) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = '/swiftlatex/PdfTeXEngine.js';
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load the LaTeX engine script'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

const createLatexEngine = async () => {
  await loadScript();
  const engine = new window.PdfTeXEngine();
  await engine.loadEngine();
  engine.setTexliveEndpoint(getTexliveEndpoint());
  await new Promise((resolve) => setTimeout(resolve, 0));
  return engine;
};

/** Reuse one WASM worker for the whole session so /tex/ package cache survives. */
export const getOrCreateLatexEngine = async () => {
  if (sharedEngine?.isReady()) return sharedEngine;
  if (sharedEngine && !sharedEngine.isReady()) {
    sharedEngine.closeWorker();
    sharedEngine = null;
    enginePromise = null;
  }
  if (!enginePromise) {
    enginePromise = createLatexEngine()
      .then((engine) => {
        sharedEngine = engine;
        return engine;
      })
      .catch((err) => {
        enginePromise = null;
        sharedEngine = null;
        throw err;
      });
  }
  return enginePromise;
};

/** Pre-download TeX packages in the background while the user picks a template. */
export const warmupLatexEngine = () => {
  if (warmupPromise) return warmupPromise;
  warmupPromise = withCompileLock(async () => {
    try {
      const result = await compileWithEngineReset(WARMUP_LATEX, { skipFlush: true });
      if (!result.pdf || result.status !== 0) {
        throw new Error('Warmup compile failed');
      }
    } catch (err) {
      warmupPromise = null;
      releaseLatexEngine();
      throw err;
    }
  });
  return warmupPromise;
};

/** Wait until warmup finishes (or fails silently). Used before first preview compile. */
export const waitForLatexWarmup = async () => {
  try {
    await warmupLatexEngine();
  } catch (_) {
    // Warmup failure should not block manual recompile attempts.
  }
};

export const releaseLatexEngine = () => {
  if (sharedEngine) {
    sharedEngine.closeWorker();
    sharedEngine = null;
  }
  enginePromise = null;
  warmupPromise = null;
};

const isEngineCrashed = (result) =>
  result.status === -254 || (result.log && result.log.includes('Engine crashed'));

const isTexCachePoisoned = (log) =>
  log && (log.includes('(/tex/null') || /l\.1 F\s*\n\s*ile not found/.test(log));

const isRecoverableCompileFailure = (result) =>
  !isTexCachePoisoned(result.log)
  && !isEngineCrashed(result)
  && (!result.pdf || result.status !== 0);

const compileLatexInner = async (engine, latexSrc, { skipFlush = false, supportFiles = {} } = {}) => {
  if (!engine || !engine.isReady()) {
    throw new Error('LaTeX engine is not ready');
  }

  const source = normalizeLatex(latexSrc);
  validateLatex(source);

  if (!skipFlush) {
    if (engine.flushCacheAsync) {
      await engine.flushCacheAsync();
    } else {
      engine.flushCache();
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  for (const [filename, fileContent] of Object.entries(supportFiles)) {
    if (engine.writeMemFSFileAsync) {
      await engine.writeMemFSFileAsync(filename, fileContent);
    } else {
      engine.writeMemFSFile(filename, fileContent);
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  if (engine.writeMemFSFileAsync) {
    await engine.writeMemFSFileAsync('main.tex', source);
  } else {
    engine.writeMemFSFile('main.tex', source);
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  if (engine.setEngineMainFileAsync) {
    await engine.setEngineMainFileAsync('main.tex');
  } else {
    engine.setEngineMainFile('main.tex');
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  let result = await engine.compileLaTeX();

  if (isEngineCrashed(result)) {
    releaseLatexEngine();
    throw new Error('LaTeX engine crashed — reload the page and try again');
  }

  // First pass often fails while font files finish downloading; retry once on the warm cache.
  if (isRecoverableCompileFailure(result)) {
    const retry = await engine.compileLaTeX();
    if (isEngineCrashed(retry)) {
      releaseLatexEngine();
      throw new Error('LaTeX engine crashed — reload the page and try again');
    }
    if (retry.pdf && retry.status === 0) {
      result = retry;
    } else if (!result.pdf && retry.pdf) {
      result = retry;
    }
  }

  // Second pass only after a successful first pass (hyperref cross-refs).
  // Never retry a failed compile — pdfTeX font-map state cannot be re-initialized.
  if (result.pdf && result.status === 0) {
    const second = await engine.compileLaTeX();
    if (isEngineCrashed(second)) {
      releaseLatexEngine();
      throw new Error('LaTeX engine crashed — reload the page and try again');
    }
    if (second.pdf) result = second;
  }

  return result;
};

const compileWithEngineReset = async (latexSrc, options) => {
  const engine = await getOrCreateLatexEngine();
  const result = await compileLatexInner(engine, latexSrc, options);
  if (!isTexCachePoisoned(result.log)) return result;

  releaseLatexEngine();
  const freshEngine = await getOrCreateLatexEngine();
  return compileLatexInner(freshEngine, latexSrc, options);
};

// Compile LaTeX source to a PDF. Returns { pdf: Uint8Array | undefined, log, status }.
export const compileLatex = (engine, latexSrc, options = {}) =>
  withCompileLock(async () => {
    const result = await compileLatexInner(engine, latexSrc, options);
    if (!isTexCachePoisoned(result.log)) return result;

    releaseLatexEngine();
    const freshEngine = await getOrCreateLatexEngine();
    return compileLatexInner(freshEngine, latexSrc, options);
  });

// Parse the page count pdfTeX reports in its log ("Output written on main.pdf (N pages...").
export const parsePageCount = (log) => {
  if (!log) return null;
  const m = log.match(/Output written on [^\n(]*\((\d+)\s+pages?/i);
  return m ? parseInt(m[1], 10) : null;
};

// Progressive condensing levels applied (from the original source) until the doc
// fits on one page. Earlier levels only tighten leading (always safe); deeper
// levels shrink the body font and, last, tighten margins to a tight floor.
// Margins are only set to a tight value (never grown), so already-compact
// templates are not made worse.
const FIT_LEVELS = [
  { linespread: 0.97 },
  { linespread: 0.94 },
  { font: [10.5, 12.6], linespread: 0.94 },
  { font: [10, 12], linespread: 0.93 },
  { font: [9.5, 11.4], linespread: 0.92 },
  { margin: '0.45in', font: [9, 10.8], linespread: 0.92 },
  { margin: '0.4in', font: [8.5, 10.2], linespread: 0.9 },
  { margin: '0.4in', font: [8, 9.6], linespread: 0.9 }
];

const applyFitLevel = (source, level) => {
  let out = source;
  const preamble = [];

  if (level.margin) {
    preamble.push(
      '\\makeatletter',
      '\\@ifpackageloaded{geometry}{}{\\usepackage{geometry}}',
      '\\makeatother',
      `\\geometry{margin=${level.margin}}`
    );
  }
  if (level.font) {
    // Redefine \normalsize so the smaller body size survives section commands and
    // \normalsize resets (a post-\begin{document} \fontsize would be overwritten).
    preamble.push(
      '\\makeatletter',
      `\\def\\normalsize{\\@setfontsize\\normalsize{${level.font[0]}pt}{${level.font[1]}pt}}`,
      '\\makeatother'
    );
  }
  if (preamble.length) {
    out = out.replace('\\begin{document}', `${preamble.join('\n')}\n\\begin{document}`);
  }

  const body = [];
  if (level.font) body.push('\\normalsize');
  if (level.linespread) body.push(`\\linespread{${level.linespread}}`);
  if (body.length) {
    body.push('\\selectfont');
    out = out.replace('\\begin{document}', `\\begin{document}\n${body.join('')}`);
  }
  return out;
};

// Last-resort: drop the lowest-priority trailing bullet (content is ordered by
// relevance, so the tail is least important). If that bullet is the only one in
// its block, remove the whole entry to avoid an empty itemize.
const trimTrailingBullet = (source) => {
  const lines = source.split('\n');
  let last = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i].trim().startsWith('\\resumeItem')) { last = i; break; }
  }
  if (last === -1) return null;

  let prev = last - 1;
  while (prev >= 0 && !lines[prev].trim()) prev--;
  let next = last + 1;
  while (next < lines.length && !lines[next].trim()) next++;

  const onlyBullet = prev >= 0 && lines[prev].trim().startsWith('\\resumeBulletsStart')
    && next < lines.length && lines[next].trim().startsWith('\\resumeBulletsEnd');

  if (onlyBullet) {
    let entry = prev - 1;
    while (entry >= 0 && !lines[entry].trim()) entry--;
    const start = entry >= 0 && lines[entry].trim().startsWith('\\resumeEntry') ? entry : prev;
    lines.splice(start, next - start + 1);
  } else {
    lines.splice(last, 1);
  }
  return lines.join('\n');
};

const isOnePage = (result) => {
  const pages = parsePageCount(result?.log);
  return pages != null && pages <= 1;
};

/**
 * Compile `source`, and if it spills past one page, progressively condense it
 * (spacing/margins/font, then trim trailing bullets) until it fits one page.
 * Returns { source, result, pages } with the final source actually rendered.
 */
export const compileToOnePage = async ({ source, supportFiles = {} } = {}) => {
  const engine = await getOrCreateLatexEngine();
  const compile = (src) => compileLatex(engine, src, { supportFiles });

  let result = await compile(source);
  // Never touch a doc that failed to compile or whose page count is unknown.
  if (!result.pdf || result.status !== 0 || parsePageCount(result.log) == null) {
    return { source, result, pages: parsePageCount(result.log) };
  }
  if (isOnePage(result)) {
    return { source, result, pages: parsePageCount(result.log) };
  }

  let best = { source, result };
  for (const level of FIT_LEVELS) {
    const candidate = applyFitLevel(source, level);
    const r = await compile(candidate);
    if (r.pdf && r.status === 0) {
      best = { source: candidate, result: r };
      if (isOnePage(r)) return { source: candidate, result: r, pages: 1 };
    }
  }

  // Still too long: trim from the strongest condense level.
  let trimmed = best.source;
  for (let i = 0; i < 60; i++) {
    const next = trimTrailingBullet(trimmed);
    if (next == null || next === trimmed) break;
    const r = await compile(next);
    if (!r.pdf || r.status !== 0) break; // trimming broke the doc — keep last good
    trimmed = next;
    best = { source: next, result: r };
    if (isOnePage(r)) break;
  }

  return { source: best.source, result: best.result, pages: parsePageCount(best.result.log) };
};

// Back-compat alias
export { createLatexEngine as createLatexEngineFresh };
